/**
 * Notifications System Hooks
 * React Query hooks for managing in-app notifications
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { NotificationType } from "@livingsyncs/types";
import apiClient from "../lib/apiClient";

export interface Notification {
  id: string;
  user_id: string;
  /** Canonical type from `@livingsyncs/types` — kept in lockstep with the backend. */
  type: NotificationType;
  title: string;
  message: string;
  resource_type?: string;
  resource_id?: string;
  metadata?: Record<string, unknown>;
  is_read: boolean;
  read_at?: string;
  /** How many times this notification recurred while unread (1 = first/only). */
  occurrence_count: number;
  created_at: string;
}

/**
 * Fetch all notifications for current user.
 *
 * Single source of truth: the full list is fetched once and the unread set is
 * derived client-side (each row carries `is_read`). Previously two queries ran
 * (`unread_only=true` + the full list), each polling on its own 30s timer,
 * which doubled the request rate for no benefit.
 */
export function useNotifications() {
  return useQuery({
    queryKey: ["notifications"],
    queryFn: async () => {
      const response = await apiClient.get<{ data: Notification[] }>(
        "/notifications",
      );
      return response.data.data;
    },
    refetchInterval: 30000, // Refetch every 30 seconds
  });
}

/**
 * Get unread notification count (derived from the single notifications query)
 */
export function useUnreadNotificationCount() {
  const { data: notifications = [] } = useNotifications();
  return notifications.filter((n) => !n.is_read).length;
}

/**
 * Mark a single notification as read
 */
export function useMarkNotificationRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (notificationId: string) => {
      const response = await apiClient.post<{ data: { success: boolean } }>(
        "/notifications/mark-read",
        { notification_id: notificationId },
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
}

/**
 * Mark all notifications as read
 */
export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await apiClient.post<{ data: { marked_count: number } }>(
        "/notifications/mark-read",
        { mark_all: true },
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
}

/**
 * Delete a notification
 */
export function useDeleteNotification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (notificationId: string) => {
      const response = await apiClient.delete<{ data: { deleted: boolean } }>(
        `/notifications?id=${notificationId}`,
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
}
